import React from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuthStatus } from '@/hooks/useApi';
import { useIsSmallScreen } from '@/hooks/useMediaQuery';
import { NotificationBell } from '@/components/notifications';
import { cn } from '@/utils/cn';
import Sidebar from './Sidebar';
import { CommandPaletteProvider } from '@/components/search/CommandPalette';
import SearchTrigger from '@/components/search/SearchTrigger';
import DemoBand from './DemoBand';
import { sysadminOnly } from './navConfig';

/** A page this deployment keeps for sysadmins (config.js SYSADMIN_ONLY_NAV): anyone else opening it by link sees
 *  that it isn't available, not the page. */
export const SysadminOnlyGuard: React.FC<{ roles: string[]; loaded: boolean; children: React.ReactNode }> = (
  { roles, loaded, children },
) => {
  const { pathname } = useLocation();
  if (loaded && sysadminOnly(pathname, roles)) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center" data-testid="sysadmin-only">
        <h1 className="text-mp-xl font-semibold text-mp-text">This page isn't available</h1>
        <p className="mt-mp-2 text-mp-base text-mp-text-muted">It isn't switched on for your account on this site.</p>
      </div>
    );
  }
  return <>{children}</>;
};

/** Remembered per user in this browser (a convenience: storage may be unavailable, and then it just isn't kept). */
export const collapseKey = (user: string) => `mp.sidebar.collapsed.${user}`;

function useCollapsed(user: string | undefined): [boolean, () => void] {
  const read = React.useCallback(() => {
    if (!user) return false;
    try { return window.localStorage.getItem(collapseKey(user)) === '1'; } catch { return false; }
  }, [user]);
  const [collapsed, setCollapsed] = React.useState(read);
  React.useEffect(() => { setCollapsed(read()); }, [read]);
  const toggle = React.useCallback(() => setCollapsed(c => {
    const next = !c;
    if (user) {
      try { window.localStorage.setItem(collapseKey(user), next ? '1' : '0'); } catch { /* not kept */ }
    }
    return next;
  }), [user]);
  return [collapsed, toggle];
}

/** The phone drawer: a modal panel over the page. Esc or the backdrop closes it, focus stays inside while it is
 *  open and goes back to the menu button after. */
function Drawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: React.ReactNode }) {
  const panel = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const back = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>('a[href], button')?.focus();
    return () => back?.focus?.();
  }, [open]);
  if (!open) return null;
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key !== 'Tab' || !panel.current) return;
    const f = Array.from(panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), select, [tabindex="0"]'));
    if (f.length === 0) return;
    const [first, last] = [f[0], f[f.length - 1]];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <div className="fixed inset-0 z-50 md:hidden" onKeyDown={onKey}>
      <div className="absolute inset-0 bg-mp-text opacity-40" onClick={onClose} data-testid="drawer-backdrop" />
      <div ref={panel} role="dialog" aria-modal="true" aria-label="Menu"
           className="absolute inset-y-0 left-0 w-mp-sidebar max-w-[85vw] shadow-xl">
        {children}
      </div>
    </div>
  );
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { data: authStatus } = useAuthStatus();
  const small = useIsSmallScreen();
  const user = authStatus?.user;
  const [collapsed, toggleCollapsed] = useCollapsed(user ? String(user.id ?? user.username) : undefined);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  React.useEffect(() => { setDrawerOpen(false); }, [pathname, small]);

  return (
    <CommandPaletteProvider>
    <div className="min-h-screen bg-mp-bg">
      {/* C64 (a11y, WCAG 2.4.1): the first stop for a keyboard: past the menu (about 16 stops) to the page */}
      <a href="#main" data-testid="skip-link"
         onClick={e => { e.preventDefault(); document.getElementById('main')?.focus(); }}
         className="sr-only focus:not-sr-only focus:fixed focus:left-mp-2 focus:top-mp-2 focus:z-50 focus:rounded-mp-sm focus:bg-mp-surface focus:px-mp-3 focus:py-mp-2 focus:text-mp-base focus:font-medium focus:text-mp-accent focus:shadow-lg focus:outline focus:outline-2 focus:outline-mp-accent">
        Skip to main content
      </a>
      {small ? (
        <>
          {/* C5: the phone's top bar is the menu band (near-black), the white logo straight on it, as in the sidebar */}
          <header className="sticky top-0 z-40 flex h-14 items-center justify-between gap-mp-2 border-b border-mp-nav-border bg-mp-nav-bg px-mp-2"
                  data-testid="phone-topbar">
            <button type="button" onClick={() => setDrawerOpen(true)} aria-label="Open menu" aria-expanded={drawerOpen}
                    className="rounded-mp-sm p-mp-2 text-mp-nav-text hover:bg-mp-nav-active-bg hover:text-mp-nav-active-text focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-mp-nav-accent">
              <Menu className="h-6 w-6" />
            </button>
            <img src="/assets/clarative-480.png" alt="Clarative" className="h-7 w-auto" />
            <span className="flex items-center">
              {user && <SearchTrigger compact />}
              {user ? <NotificationBell /> : <span className="w-10" />}
            </span>
          </header>
          <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
            <Sidebar variant="drawer" onClose={() => setDrawerOpen(false)} onNavigate={() => setDrawerOpen(false)} />
          </Drawer>
        </>
      ) : (
        <aside className={cn('fixed inset-y-0 left-0 z-30 transition-[width] duration-150',
                             collapsed ? 'w-mp-sidebar-collapsed' : 'w-mp-sidebar')}>
          <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
        </aside>
      )}
      <div className={cn(!small && (collapsed ? 'pl-mp-sidebar-collapsed' : 'pl-mp-sidebar'))}>
        <DemoBand className={cn('sticky z-20', small ? 'top-14' : 'top-0')} user={user} />
        <main id="main" tabIndex={-1} className="min-w-0 px-4 py-6 focus:outline-none lg:px-6">
          <SysadminOnlyGuard roles={user?.roles ?? []} loaded={!!authStatus}>{children}</SysadminOnlyGuard>
        </main>
      </div>
    </div>
    </CommandPaletteProvider>
  );
};

export default Layout;
